import posthog from "posthog-js";

/** PostHog group type for Twilanswer organizations. */
export const POSTHOG_ORG_GROUP_TYPE = "ta_org";

let lastSyncedOrgId = null;

function unwrap(value) {
  if (value == null) return null;
  if (typeof value === "string") return value;
  return value.S ?? value.N ?? null;
}

/**
 * Resolve an organization id from org objects or API payloads.
 * Supports orgId, org_id, and organization_id in DynamoDB or plain shapes.
 */
export function getOrgId(org) {
  if (!org) return null;
  if (typeof org === "string") return org;
  return unwrap(org.orgId ?? org.org_id ?? org.organization_id);
}

function getOrgName(org) {
  if (!org) return null;
  return unwrap(org.organization_name ?? org.org_name);
}

function getSubscriptionStatus(org) {
  if (!org) return null;
  return (
    org.subscription_details?.M?.sub_status?.S ??
    unwrap(org.subscription_details?.sub_status)
  );
}

function getPlanName(org) {
  if (!org) return null;
  return (
    org.subscription_details?.M?.plan?.S ??
    unwrap(org.subscription_details?.plan)
  );
}

export function getOrgGroupProperties(org) {
  const orgId = getOrgId(org);
  if (!orgId) return null;

  const properties = {
    name: getOrgName(org),
    org_id: orgId,
    organization_id: orgId,
    subscription_status: getSubscriptionStatus(org),
    plan: getPlanName(org),
  };

  return Object.fromEntries(
    Object.entries(properties).filter(([, value]) => value != null)
  );
}

/**
 * Associate the active session with a ta_org group in PostHog.
 * Subsequent events are attributed to this organization until changed.
 */
export function setOrganizationGroup(org) {
  const orgId = getOrgId(org);
  if (!orgId) return;

  const properties = getOrgGroupProperties(org);
  posthog.group(POSTHOG_ORG_GROUP_TYPE, orgId, properties);
  lastSyncedOrgId = orgId;
}

export function identifyPostHogUser(user, org = null) {
  if (!user) return;

  const userId = unwrap(user.userId);
  if (!userId) return;

  const email = unwrap(user.email);
  const firstName = unwrap(user.fName ?? user.first_name);
  const lastName = unwrap(user.lName ?? user.last_name);
  const resolvedOrg =
    org ??
    (typeof window !== "undefined"
      ? JSON.parse(localStorage.getItem("organization") || "null")
      : null);

  const personProperties = {};
  if (email) personProperties.email = email;
  if (firstName || lastName) {
    personProperties.name = [firstName, lastName].filter(Boolean).join(" ");
  }

  const orgId = getOrgId(resolvedOrg);
  if (orgId) {
    personProperties.org_id = orgId;
    personProperties.organization_id = orgId;
    const orgName = getOrgName(resolvedOrg);
    if (orgName) personProperties.org_name = orgName;
  }

  posthog.identify(userId, personProperties);

  if (resolvedOrg) {
    setOrganizationGroup(resolvedOrg);
  }
}

/**
 * Sync PostHog person + ta_org group from localStorage.
 * Safe to call on navigation; skips work when the org has not changed.
 */
export function syncPostHogFromStorage({ force = false } = {}) {
  if (typeof window === "undefined") return;

  try {
    const userJson = localStorage.getItem("user");
    const orgJson = localStorage.getItem("organization");
    if (!userJson) return;

    const user = JSON.parse(userJson);
    const org = orgJson ? JSON.parse(orgJson) : null;
    const orgId = getOrgId(org);

    if (!force && orgId && orgId === lastSyncedOrgId) {
      return;
    }

    identifyPostHogUser(user, org);
  } catch {
    // Ignore malformed localStorage during auth transitions.
  }
}

/** Called whenever organization data is persisted in the app. */
export function syncPostHogOrganization(org) {
  if (!org) return;

  const userJson =
    typeof window !== "undefined" ? localStorage.getItem("user") : null;

  if (userJson) {
    identifyPostHogUser(JSON.parse(userJson), org);
    return;
  }

  setOrganizationGroup(org);
}
